import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useSortableResults, ScoreBar } from '../search/useSortableResults';
import PersonResultDialog from './PersonResultDialog';
import type { PersonResult } from '../../types/api';

type SortKey = 'index' | 'similarity' | 'name' | 'faculty';

interface PeopleResultsTableProps {
  results: PersonResult[];
}

export default function PeopleResultsTable({ results }: PeopleResultsTableProps) {
  const { t } = useTranslation('search');
  const { t: tPerson } = useTranslation('person');
  const [dialogResult, setDialogResult] = useState<PersonResult | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const { sortedResults, HeaderButton } = useSortableResults<PersonResult, SortKey>(results);

  const openDialogFor = (result: PersonResult, e: React.MouseEvent) => {
    if (e.metaKey || e.ctrlKey) return;
    e.preventDefault();
    setDialogResult(result);
    setDialogOpen(true);
  };

  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-12 text-center">
              <HeaderButton column="index" label={t('table.no')} align="center" />
            </TableHead>
            <TableHead className="w-28 text-center">
              <TooltipProvider delayDuration={300}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="inline-block">
                      <HeaderButton column="similarity" label={t('table.similarity')} align="center" />
                    </span>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="max-w-xs text-xs">
                    {tPerson('help.readingResults.similarityHeaderTooltip')}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </TableHead>
            <TableHead>
              <HeaderButton column="name" label={t('table.name')} />
            </TableHead>
            <TableHead className="w-32">
              <HeaderButton column="faculty" label={t('table.faculty')} />
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {sortedResults.map((result, index) => (
            <TableRow key={result.id}>
              <TableCell className="text-center text-muted-foreground tabular-nums">
                {index + 1}
              </TableCell>
              <TableCell className="text-center">
                <div className="inline-flex items-center gap-2">
                  <span className="font-medium tabular-nums">
                    {typeof result.similarity === 'number'
                      ? result.similarity.toFixed(2)
                      : result.similarity}
                  </span>
                  <ScoreBar value={typeof result.similarity === 'number' ? result.similarity : null} />
                </div>
              </TableCell>
              <TableCell>
                <a
                  href={`/person/${result.id}`}
                  onClick={(e) => openDialogFor(result, e)}
                  className="font-medium text-primary hover:underline"
                >
                  {result.name}
                </a>
              </TableCell>
              <TableCell className="text-muted-foreground">{result.faculty}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <PersonResultDialog
        result={dialogResult}
        open={dialogOpen}
        onOpenChange={setDialogOpen}
      />
    </div>
  );
}
